import React, { useState } from 'react';
import { Zap, Droplet, Pencil, Check, X } from 'lucide-react';
import { formatEuro, formatNumber, formatPrezzoUnitario, formatPeriod } from '../../../utils/formatters';
import { LOGO_URL } from '../../../utils/constants';

/**
 * Tabella di confronto costi concorrente vs Energy.Lab.
 * Comprende: header colonne, info consumo, breakdown costi, IVA, totali.
 * @param {Object} props
 * @param {Object} props.analysis - analisi completa
 * @param {Function} [props.onEditExtracted] - callback per inline-edit di client_name e iva_rate.
 *   Ricevuto solo in modalità VOLATILE (no finalizzato). Firma: ({client_name?, iva_rate?}) => Promise
 */
export const ComparisonTable = ({ analysis, onEditExtracted, showCte = true }) => {
  const comp = analysis.competitor_data;
  const sim = analysis.energylab_simulation;
  const isGas = comp.service_type === 'gas';
  const energyLabel = isGas ? 'Gas' : 'Energia';

  return (
    <div className="el-card overflow-hidden">
      {/* Intestazioni colonne */}
      <div className="grid grid-cols-2">
        <div className="p-4 bg-[#F4F7F4] border-b border-r border-[#DCE3DC]">
          <h3 className="font-medium text-[#1B291B]">{(comp.supplier_name || 'FORNITORE ATTUALE').toUpperCase()}</h3>
          <ClientNameField name={comp.client_name} onEdit={onEditExtracted} />
        </div>
        <div className="p-4 border-b border-[#DCE3DC] flex items-center justify-center" style={{ backgroundColor: '#E8F5E9' }}>
          <img src={LOGO_URL} alt="Energy.Lab" style={{ height: '28px', width: 'auto', display: 'block' }} data-pdf-img />
        </div>
      </div>

      {/* Info consumo */}
      <div className="p-4 border-b border-[#DCE3DC] bg-white">
        <div className="flex items-center gap-2 text-sm text-[#5C6E5C] flex-wrap">
          {isGas ? <Droplet className="w-4 h-4 text-sky-400 fill-sky-400" /> : <Zap className="w-4 h-4 text-yellow-500" />}
          <span>Consumo: {formatNumber(comp.consumption, 0)} {comp.consumption_unit}</span>
          <span className="mx-2">&bull;</span>
          <span>Periodo: {formatPeriod(comp.billing_period_start, comp.billing_period_end)}</span>
          {showCte && (
            <>
              <span className="mx-2">&bull;</span>
              <span data-testid="cte-code">{analysis.selected_cte}</span>
            </>
          )}
          {comp.sii_code && (
            <>
              <span className="mx-2">&bull;</span>
              <span data-testid="pod-pdr">
                <strong>{isGas ? 'PDR' : 'POD'}:</strong> {comp.sii_code}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Breakdown costi */}
      <div className="divide-y divide-[#DCE3DC]" data-testid="comparison-table">
        {/* Spesa energia/gas consumi — evidenziato (giallo energia / azzurro gas) */}
        <HighlightedRow
          isGas={isGas}
          label={`Spesa ${energyLabel} Consumi`}
          leftValue={formatEuro(comp.spesa_energia_consumi || 0)}
          leftSub={`(${formatPrezzoUnitario(analysis.competitor_prezzo_medio || ((comp.spesa_energia_consumi || 0) / (comp.consumption || 1)))} €/${comp.consumption_unit})`}
          rightValue={formatEuro(sim.spesa_energia_consumi)}
          rightSub={`(${formatPrezzoUnitario(analysis.energylab_prezzo_medio || (sim.spesa_energia_consumi / (comp.consumption || 1)))} €/${comp.consumption_unit})`}
        />

        {/* Quota fissa — evidenziato (giallo energia / azzurro gas) */}
        <HighlightedRow
          isGas={isGas}
          label="Quota Fissa"
          leftValue={formatEuro(comp.spesa_energia_quota_fissa || 0)}
          rightValue={formatEuro(sim.spesa_energia_quota_fissa)}
        />

        {/* Quota potenza — solo energia */}
        {!isGas && (
          <StandardRow label="Quota Potenza" leftValue={formatEuro(comp.spesa_energia_quota_potenza || 0)} rightValue={formatEuro(sim.spesa_energia_quota_potenza)} />
        )}

        <StandardRow label="Spese Rete e Oneri" leftValue={formatEuro(comp.spesa_rete_oneri || 0)} rightValue={formatEuro(sim.spesa_rete_oneri)} />

        {/* Energia Reattiva — ribaltata in Energy.Lab (voce regolata) */}
        {((comp.energia_reattiva != null && comp.energia_reattiva !== 0) || (sim.energia_reattiva != null && sim.energia_reattiva !== 0)) && (
          <StandardRow
            label="Energia Reattiva"
            leftValue={formatEuro(comp.energia_reattiva || 0)}
            rightValue={formatEuro(sim.energia_reattiva || 0)}
          />
        )}

        <StandardRow label="Altre Partite" leftValue={formatEuro(comp.altre_partite || 0)} rightValue={formatEuro(sim.altre_partite || 0)} />
        <StandardRow label="Accise" leftValue={formatEuro(comp.accise || 0)} rightValue={formatEuro(sim.accise)} />

        {/* Canone RAI */}
        {comp.canone_rai != null && comp.canone_rai !== 0 && (
          <div className="grid grid-cols-2" data-testid="canone-rai-row">
            <div className="p-3 border-r border-[#DCE3DC] bg-amber-50/50">
              <p className="text-xs text-[#5C6E5C]">Canone RAI</p>
              <p className="font-medium">{formatEuro(comp.canone_rai)}</p>
              <p className="text-[10px] text-amber-600">Fuori campo IVA art.2</p>
            </div>
            <div className="p-3 bg-[#F0FDF4]">
              <p className="text-xs text-[#5C6E5C]">Canone RAI</p>
              <p className="font-medium">{formatEuro(sim.canone_rai || 0)}</p>
              <p className="text-[10px] text-[#5C6E5C]">Fuori campo IVA art.2</p>
            </div>
          </div>
        )}

        {/* Interessi di mora */}
        {comp.interessi_mora != null && comp.interessi_mora !== 0 && (
          <div className="grid grid-cols-2" data-testid="interessi-mora-row">
            <div className="p-3 border-r border-[#DCE3DC] bg-red-50/50">
              <p className="text-xs text-[#5C6E5C]">Interessi di Mora</p>
              <p className="font-medium">{formatEuro(comp.interessi_mora)}</p>
              <p className="text-[10px] text-red-500">Esclusa art. 15</p>
            </div>
            <div className="p-3 bg-[#F0FDF4]">
              <p className="text-xs text-[#5C6E5C]">Interessi di Mora</p>
              <p className="font-medium text-[#5C6E5C]">-</p>
            </div>
          </div>
        )}

        {/* Arrotondamenti */}
        {comp.arrotondamenti != null && comp.arrotondamenti !== 0 && (
          <div className="grid grid-cols-2" data-testid="arrotondamenti-row">
            <div className="p-3 border-r border-[#DCE3DC]">
              <p className="text-xs text-[#5C6E5C]">Arrotondamenti</p>
              <p className="font-medium">{formatEuro(comp.arrotondamenti)}</p>
              <p className="text-[10px] text-[#5C6E5C]">Fuori campo IVA</p>
            </div>
            <div className="p-3 bg-[#F0FDF4]">
              <p className="text-xs text-[#5C6E5C]">Arrotondamenti</p>
              <p className="font-medium text-[#5C6E5C]">-</p>
            </div>
          </div>
        )}

        {/* Imposta di Bollo — NON ribaltata in Energy.Lab: applicata solo se ci sono voci esenti IVA (mora)
            e noi la mora non la portiamo */}
        {comp.imposta_bollo != null && comp.imposta_bollo !== 0 && (
          <div className="grid grid-cols-2" data-testid="imposta-bollo-row">
            <div className="p-3 border-r border-[#DCE3DC] bg-red-50/50">
              <p className="text-xs text-[#5C6E5C]">Imposta di Bollo</p>
              <p className="font-medium">{formatEuro(comp.imposta_bollo)}</p>
              <p className="text-[10px] text-red-500">Su voci esenti IVA (mora)</p>
            </div>
            <div className="p-3 bg-[#F0FDF4]">
              <p className="text-xs text-[#5C6E5C]">Imposta di Bollo</p>
              <p className="font-medium text-[#5C6E5C]">-</p>
              <p className="text-[10px] text-[#5C6E5C]">Nessuna voce esente IVA</p>
            </div>
          </div>
        )}

        {/* Bonus/Sconti — applicati solo dal fornitore, NON ribaltati in Energy.Lab */}
        {comp.bonus_sconti != null && comp.bonus_sconti !== 0 && (
          <div className="grid grid-cols-2" data-testid="bonus-sconti-row">
            <div className="p-3 border-r border-[#DCE3DC] bg-blue-50/50">
              <p className="text-xs text-[#5C6E5C]">Bonus / Sconti fornitore</p>
              <p className="font-medium text-blue-700">{formatEuro(comp.bonus_sconti)}</p>
              <p className="text-[10px] text-blue-500">Sconto commerciale</p>
            </div>
            <div className="p-3 bg-[#F0FDF4]">
              <p className="text-xs text-[#5C6E5C]">Bonus / Sconti fornitore</p>
              <p className="font-medium text-[#5C6E5C]">-</p>
              <p className="text-[10px] text-[#5C6E5C]">Non applicabile</p>
            </div>
          </div>
        )}

        {/* Bonus Sociale — previsione normativa, ribaltato ANCHE in Energy.Lab */}
        {((comp.bonus_sociale != null && comp.bonus_sociale !== 0) || (sim.bonus_sociale != null && sim.bonus_sociale !== 0)) && (
          <div className="grid grid-cols-2" data-testid="bonus-sociale-row">
            <div className="p-3 border-r border-[#DCE3DC] bg-purple-50/50">
              <p className="text-xs text-[#5C6E5C]">Bonus Sociale</p>
              <p className="font-medium text-purple-700">{formatEuro(comp.bonus_sociale || 0)}</p>
            </div>
            <div className="p-3 bg-[#F0FDF4]">
              <p className="text-xs text-[#5C6E5C]">Bonus Sociale</p>
              <p className="font-medium text-[#4CAF50]">{formatEuro(sim.bonus_sociale || 0)}</p>
            </div>
          </div>
        )}

        {/* IVA */}
        <IvaSection competitorData={comp} simulation={sim} onEditIvaRate={onEditExtracted} />

        {/* Totali */}
        <div className="grid grid-cols-2">
          <div className="p-4 border-r border-[#DCE3DC] bg-[#F4F7F4]">
            <p className="text-xs text-[#5C6E5C] mb-1">TOTALE BOLLETTA</p>
            <p className="text-2xl font-medium">{formatEuro(comp.total || 0)}</p>
          </div>
          <div className="p-4 bg-[#4CAF50] text-white">
            <p className="text-xs opacity-80 mb-1">TOTALE ENERGY.LAB</p>
            <p className="text-2xl font-medium">{formatEuro(sim.total)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Riga evidenziata: giallo ambra per energia, azzurro per gas */
const HighlightedRow = ({ isGas, label, leftValue, leftSub, rightValue, rightSub }) => {
  // Palette differenziata per tipo servizio (mantengo la stessa struttura visuale)
  const palette = isGas
    ? { bgLeft: 'bg-sky-50', bgRight: 'bg-sky-100', labelText: 'text-sky-700', mainText: 'text-sky-900', subText: 'text-sky-600' }
    : { bgLeft: 'bg-amber-50', bgRight: 'bg-amber-100', labelText: 'text-amber-700', mainText: 'text-amber-900', subText: 'text-amber-600' };
  return (
    <div className={`grid grid-cols-2 ${palette.bgLeft}`}>
      <div className="p-3 border-r border-[#DCE3DC]">
        <p className={`text-xs font-medium ${palette.labelText}`}>{label}</p>
        <p className={`font-semibold ${palette.mainText}`}>{leftValue}</p>
        {leftSub && <p className={`text-xs ${palette.subText}`}>{leftSub}</p>}
      </div>
      <div className={`p-3 ${palette.bgRight}`}>
        <p className={`text-xs font-medium ${palette.labelText}`}>{label}</p>
        <p className="font-semibold text-[#4CAF50]">{rightValue}</p>
        {rightSub && <p className={`text-xs ${palette.subText}`}>{rightSub}</p>}
      </div>
    </div>
  );
};

/** Riga standard confronto */
const StandardRow = ({ label, leftValue, rightValue }) => (
  <div className="grid grid-cols-2">
    <div className="p-3 border-r border-[#DCE3DC]">
      <p className="text-xs text-[#5C6E5C]">{label}</p>
      <p className="font-medium">{leftValue}</p>
    </div>
    <div className="p-3 bg-[#F0FDF4]">
      <p className="text-xs text-[#5C6E5C]">{label}</p>
      <p className="font-medium text-[#4CAF50]">{rightValue}</p>
    </div>
  </div>
);

/** Sezione IVA: singola o a scaglioni */
const IvaSection = ({ competitorData: comp, simulation: sim, onEditIvaRate }) => {
  if (comp.iva_scaglioni && comp.iva_scaglioni.length > 1) {
    return (
      <>
        {comp.iva_scaglioni.map((scag, idx) => (
          <div key={idx} className="grid grid-cols-2" data-testid={`iva-scaglione-row-${idx}`}>
            <div className="p-3 border-r border-[#DCE3DC]">
              <p className="text-xs text-[#5C6E5C]">IVA ({scag.rate}%)</p>
              <p className="font-medium">{formatEuro(scag.amount || 0)}</p>
              <p className="text-[10px] text-[#5C6E5C]">Imp. {formatEuro(scag.imponibile || 0)}</p>
            </div>
            <div className="p-3 bg-[#F0FDF4]">
              <p className="text-xs text-[#5C6E5C]">IVA ({scag.rate}%)</p>
              <p className="font-medium">{formatEuro(sim.iva_scaglioni?.[idx]?.amount || 0)}</p>
              <p className="text-[10px] text-[#5C6E5C]">Imp. {formatEuro(sim.iva_scaglioni?.[idx]?.imponibile || 0)}</p>
            </div>
          </div>
        ))}
        <div className="grid grid-cols-2">
          <div className="p-3 border-r border-[#DCE3DC] bg-[#F9FAFB]">
            <p className="text-xs text-[#5C6E5C] font-semibold">Totale IVA</p>
            <p className="font-medium">{formatEuro(comp.iva_amount || 0)}</p>
          </div>
          <div className="p-3 bg-[#E8F5E9]">
            <p className="text-xs text-[#5C6E5C] font-semibold">Totale IVA</p>
            <p className="font-medium">{formatEuro(sim.iva_amount)}</p>
          </div>
        </div>
      </>
    );
  }

  const currentRate = comp.iva_rate || (comp.iva_scaglioni?.[0]?.rate) || 22;
  return (
    <div className="grid grid-cols-2" data-testid="iva-single-row">
      <div className="p-3 border-r border-[#DCE3DC]">
        <div className="text-xs text-[#5C6E5C] flex items-center gap-1">
          <span>IVA</span>
          <IvaRateField rate={currentRate} onEdit={onEditIvaRate} />
        </div>
        <p className="font-medium">{formatEuro(comp.iva_amount || 0)}</p>
      </div>
      <div className="p-3 bg-[#F0FDF4]">
        <p className="text-xs text-[#5C6E5C]">IVA ({sim.iva_rate}%)</p>
        <p className="font-medium">{formatEuro(sim.iva_amount)}</p>
      </div>
    </div>
  );
};

// ─── Inline-edit fields ──────────────────────────────────
/** Nome cliente editabile (solo in modalità volatile). */
const ClientNameField = ({ name, onEdit }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  if (!onEdit) {
    return <p className="text-sm text-[#5C6E5C]" data-testid="client-name">{name}</p>;
  }

  const startEdit = () => {
    setValue(name || '');
    setEditing(true);
  };
  const save = async () => {
    const v = value.trim();
    if (!v || v === name) { setEditing(false); return; }
    setSaving(true);
    try {
      await onEdit({ client_name: v });
      setEditing(false);
    } catch (_e) {/* errore già notificato */}
    setSaving(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1" data-testid="client-name-editor">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="flex-1 min-w-0 px-2 py-1 text-sm border border-[#4CAF50] rounded-md focus:outline-none focus:ring-2 focus:ring-[#4CAF50]/30"
          autoFocus
          disabled={saving}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          data-testid="client-name-input"
        />
        <button onClick={save} disabled={saving} className="p-1 rounded-md bg-[#4CAF50] text-white hover:bg-[#3D8C40] disabled:opacity-50" title="Salva" data-testid="client-name-save">
          <Check className="w-3 h-3" />
        </button>
        <button onClick={() => setEditing(false)} disabled={saving} className="p-1 rounded-md bg-white border border-[#DCE3DC] text-[#5C6E5C] hover:bg-[#F4F7F4]" title="Annulla" data-testid="client-name-cancel">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <p className="text-sm text-[#5C6E5C] flex items-center gap-1 group" data-testid="client-name">
      {name}
      <button onClick={startEdit} className="ml-1 p-0.5 rounded hover:bg-[#4CAF50]/20 opacity-50 hover:opacity-100 transition-opacity" title="Modifica nome cliente" data-testid="client-name-edit-btn">
        <Pencil className="w-3 h-3" />
      </button>
    </p>
  );
};

/** Aliquota IVA editabile (10% o 22%) — solo in modalità volatile. */
const IvaRateField = ({ rate, onEdit }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!onEdit) {
    return <span data-testid="iva-rate-display">({rate}%)</span>;
  }

  const save = async (newRate) => {
    if (newRate === rate) { setEditing(false); return; }
    setSaving(true);
    try {
      await onEdit({ iva_rate: newRate });
      setEditing(false);
    } catch (_e) {/* errore già notificato */}
    setSaving(false);
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1" data-testid="iva-rate-editor">
        <button onClick={() => save(10)} disabled={saving} className={`px-2 py-0.5 text-xs rounded-md border ${rate === 10 ? 'bg-[#4CAF50] text-white border-[#4CAF50]' : 'bg-white text-[#1B291B] border-[#DCE3DC] hover:bg-[#F4F7F4]'}`} data-testid="iva-rate-10">
          10%
        </button>
        <button onClick={() => save(22)} disabled={saving} className={`px-2 py-0.5 text-xs rounded-md border ${rate === 22 ? 'bg-[#4CAF50] text-white border-[#4CAF50]' : 'bg-white text-[#1B291B] border-[#DCE3DC] hover:bg-[#F4F7F4]'}`} data-testid="iva-rate-22">
          22%
        </button>
        <button onClick={() => setEditing(false)} disabled={saving} className="p-0.5 rounded-md bg-white border border-[#DCE3DC] text-[#5C6E5C] hover:bg-[#F4F7F4]" title="Annulla" data-testid="iva-rate-cancel">
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1" data-testid="iva-rate-display">
      ({rate}%)
      <button onClick={() => setEditing(true)} className="p-0.5 rounded hover:bg-[#4CAF50]/20 opacity-50 hover:opacity-100 transition-opacity" title="Modifica aliquota IVA" data-testid="iva-rate-edit-btn">
        <Pencil className="w-3 h-3" />
      </button>
    </span>
  );
};
